import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
// @mui
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import Button from 'src/components/Button';
// utils
import { useQuery } from '@apollo/client';
import Alert from '@mui/material/Alert';
import { useParams } from 'react-router';
import FormProvider, { RHFAutocomplete, RHFSelect, RHFTextField } from 'src/components/hook-form';
import STATES from 'src/constants/states';
import { GET_TASK_IDS } from 'src/graphql/tasks/queries';
import { assignableTaskConditions } from 'src/utils/task-helper';

const Section = ({ title, children }) => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography paragraph fontWeight={600}>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
};

export default function AdminuserForm({
  inCampaigns = false,
  loading,
  user,
  disableFields,
  email,
  orgRole,
  campaigns,
  tasks,
  onSubmit,
}) {
  const { orgId } = useParams();
  // const { data: campaignData } = useQuery(GET_CAMPAIGNS, {
  //   variables: {
  //     orgId,
  //   },
  // });
  const { data: tasksData } = useQuery(GET_TASK_IDS, {
    variables: {
      query: {
        _and: [
          {
            tasks_task_campaigns: {
              task_campaigns_campaign: {
                campaigns_organization: {
                  organization_id: {
                    _eq: orgId,
                  },
                },
              },
            },
          },
          ...assignableTaskConditions(),
        ],
      },
    },
  });

  const schema = Yup.object().shape({
    first_name: Yup.string().required('First Name is required'),
    last_name: Yup.string().required('Last Name is required'),
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
  });
  const defaultValues = useMemo(
    () => ({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || email || '',
      status: user?.status || 'active',
      phone: user?.phone || '',
      address: user?.address || '',
      address2: user?.address2 || '',
      city: user?.city || '',
      state: user?.state || '',
      zip: user?.zip || '',
      org_role: orgRole || '',
      user_role: user?.users_user_roles[0]?.user_roles_roles?.code || '',
      // campaigns:
      //   campaigns && campaigns.length
      //     ? campaigns?.map((c) => ({
      //         label: c.user_campaigns_campaign.name,
      //         value: c.user_campaigns_campaign.campaign_id,
      //       }))
      //     : [],
      tasks:
        tasks?.length > 0
          ? tasks?.map((t) => ({
              label: t?.user_tasks_task?.name,
              value: t?.user_tasks_task?.task_id,
            }))
          : [],
    }),
    [user, orgRole, tasks]
  );
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) {
      methods.reset(defaultValues);
    }
  }, [defaultValues]);

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const formSubmit = (data) => {
    if (inCampaigns) {
      data = {
        ...data,
        user_role: 'manager',
        org_role: 'owner',
      };
    }

    onSubmit(data);
  };

  const userRole = methods.watch('user_role');

  useEffect(() => {
    if (userRole) {
      if (userRole === 'manager' || userRole === 'legislative_member') {
        methods.setValue('org_role', 'owner');
      } else {
        methods.setValue('org_role', 'volunteer');
      }
    } else {
      methods.setValue('org_role', '');
    }
  }, [userRole, methods]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
      <Section title="Contact Information">
        {!user && (
          <Alert severity="info" sx={{ mb: 2 }}>
            An email invite will be sent with login information
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item md={6}>
            <RHFTextField fullWidth name="first_name" label="First Name" disabled={disableFields} />
          </Grid>
          <Grid item md={6}>
            <RHFTextField fullWidth name="last_name" label="Last Name" disabled={disableFields} />
          </Grid>
          <Grid item md={6}>
            <RHFTextField
              fullWidth
              type="email"
              name="email"
              label="Email"
              disabled={disableFields}
            />
          </Grid>
          <Grid item md={6}>
            <RHFTextField fullWidth name="phone" label="Phone" disabled={disableFields} />
          </Grid>
          <Grid item md={6}>
            <RHFTextField fullWidth name="address" label="Address" disabled={disableFields} />
          </Grid>
          <Grid item md={6}>
            <RHFTextField
              fullWidth
              name="address2"
              label="App/Unit/Other"
              disabled={disableFields}
            />
          </Grid>
          <Grid item md={4}>
            <RHFTextField fullWidth name="city" label="City" disabled={disableFields} />
          </Grid>
          <Grid item md={4}>
            <RHFSelect native fullWidth name="state" label="State" disabled={disableFields}>
              <option selected>Select</option>
              {STATES.map((state) => (
                <option value={state.abbreviation} key={state.abbreviation}>
                  {state.name}
                </option>
              ))}
            </RHFSelect>
          </Grid>
          <Grid item md={4}>
            <RHFTextField fullWidth name="zip" label="Zip Code" disabled={disableFields} />
          </Grid>
        </Grid>
      </Section>
      <Section title={'Organization Information'}>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item md={6}>
            <RHFSelect native name="user_role" label="Primary Role">
              <option selected>Select</option>
              <option value="manager">Manager</option>
              <option value="user">Volunteer</option>
              <option value="legislative_member">Legislative Member</option>
            </RHFSelect>
          </Grid>
          {userRole === 'user' ? (
            <Grid item md={6}>
              <RHFAutocomplete
                fullWidth
                multiple
                name="tasks"
                label="Tasks"
                options={
                  tasksData?.rows.map((row) => {
                    return {
                      label: row.name,
                      value: row.task_id,
                    };
                  }) ?? []
                }
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Grid>
          ) : null}
        </Grid>
      </Section>
      <Stack direction="row" justifyContent="flex-end">
        <Button loading={loading} type="submit">
          {user ? 'Update' : 'Add User'}
        </Button>
      </Stack>
    </FormProvider>
  );
}
